import { useCallback } from 'react';
import type { ComponentPropsWithoutRef, FC } from 'react';
import {
  ButtonList,
  ServerNotification,
  SocialSignInLabel,
  FormContainer,
} from './styled';
import { Apple, Facebook, Google } from '@components/OAuthButtons';
import { Button } from '@simplywallst/ui-core';
import If from '@components/If';
import { useLogin, useRegister } from './hooks';
import loadable from '@loadable/component';
import { useOSDetect } from '@/hooks/useOSDetect';
import { useRewardfulAffiliate } from '@/hooks/useRewardful';
import { useHotjar } from '@/hooks/useHotjar';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTE_COMPANY, ROUTE_COMPANY_WITH_EVENTS } from '@/constants/routes';
import { getLocation } from '@/redux/location/selectors';

const RegistrationForm = loadable(() => import('../RegistrationForm'));
const LoginForm = loadable(() => import('../LoginForm'));
const Select = loadable(() => import('../AuthOptions'), {
  resolveComponent: (components) => components.AuthOptions,
});

enum AuthType {
  SWS = 'sws',
  Apple = 'Apple',
  Facebook = 'Facebook',
  Google = 'Google',
}

export enum Type {
  Select,
  Login,
  Register,
}

interface Props {
  current?: Type;
  onChangeType?: (type: Type) => void;
  onRegisterSuccess?: () => void;
  onLoginSuccess?: () => void;
  onRegisterError?: (error: unknown) => void;
  onLoginError?: (error: unknown) => void;
}

export const AuthenticationSelect: FC<Props> = ({
  onRegisterSuccess = () => null,
  onLoginSuccess = () => null,
  onLoginError,
  onRegisterError,
  onChangeType,
  current,
}) => {
  const [hotjar] = useHotjar();
  const affiliate = useRewardfulAffiliate();
  const referralData = affiliate?.token;
  const os = useOSDetect();
  const dispatch = useDispatch();
  const location = useSelector(getLocation);
  const companyPageType = ROUTE_COMPANY || ROUTE_COMPANY_WITH_EVENTS;

  // Execute non-critical path actions after a user Registers
  const handleRegisterSuccess = (response) => {
    console.log('handleRegisterSuccess', { response });
    dispatch({
      type: 'UI__START_ONBOARDING',
      payload: {
        registeredOnCompanyPage: location.type === companyPageType,
      },
    });
    try {
      onRegisterSuccess();
    } catch (err) {}
    try {
      hotjar?.('identify', response.data.id, {
        register_date: new Date().toISOString(),
      });
    } catch (err) {}
  };

  // Execute non-critical path actions after a user Login
  const handleLoginSuccess = (response) => {
    console.log('handleLoginSuccess', { response });

    // Social rego is technically a "login" not a "rego", so fork here to
    // allow that registration type to pass through register success callback
    if (response.data.created) {
      handleRegisterSuccess(response);
    } else {
      onLoginSuccess();
    }
  };

  const {
    mutate: createAccount,
    status: createAccountStatus,
    error: createAccountError,
  } = useRegister({
    onSuccess: handleRegisterSuccess,
    onError: onRegisterError,
  });
  const {
    mutate: login,
    status: loginStatus,
    error: loginError,
  } = useLogin({
    onSuccess: handleLoginSuccess,
    onError: onLoginError,
  });

  const handleAppleSuccess = useCallback<
    ComponentPropsWithoutRef<typeof Apple>['onSuccess']
  >(
    (token, user) => login({ type: AuthType.Apple, token, user, referralData }),
    [login, referralData]
  );

  const handleFacebookSuccess = useCallback<
    ComponentPropsWithoutRef<typeof Facebook>['onSuccess']
  >(
    (token) => login({ type: AuthType.Facebook, token, referralData }),
    [login, referralData]
  );

  const handleGoogleSuccess = useCallback<
    ComponentPropsWithoutRef<typeof Google>['onSuccess']
  >(
    (token) => {
      // NOTE: login is async and not awaited here
      login({ type: AuthType.Google, token, referralData });
    },
    [login, referralData]
  );

  const handleLoginSubmit = useCallback<
    ComponentPropsWithoutRef<typeof LoginForm>['onSubmit']
  >(
    (email, password) =>
      login({ type: AuthType.SWS, username: email, password }),
    [login]
  );

  const handleRegistration = useCallback<
    ComponentPropsWithoutRef<typeof RegistrationForm>['onSubmit']
  >(
    (email, password, firstName) => {
      createAccount({
        email,
        password,
        firstName,
        referralData,
      });
    },
    [createAccount, referralData]
  );

  const handleOptionSelect = useCallback<
    ComponentPropsWithoutRef<typeof Select>['onSuccess']
  >(
    (options) => {
      switch (options.provider) {
        case 'google':
          handleGoogleSuccess(options.token);
          break;
        case 'facebook':
          handleFacebookSuccess(options.token);
          break;
        case 'apple':
          handleAppleSuccess(options.token, options.user);
          break;
      }
    },
    [handleGoogleSuccess, handleFacebookSuccess, handleAppleSuccess]
  );

  const globalDisable =
    createAccountStatus === 'loading' || loginStatus === 'loading';

  return (
    <>
      {loginStatus === 'error' && (
        <ServerNotification alert small data-cy-id="login-notification-error">
          {loginError?.toString()}
        </ServerNotification>
      )}
      {createAccountStatus === 'error' && (
        <ServerNotification
          alert
          small
          data-cy-id="registration-notification-error"
        >
          {createAccountError?.toString()}
        </ServerNotification>
      )}
      <If
        conditions={{
          select: current === Type.Select,
          loginRegister: current !== Type.Select,
        }}
      >
        {{
          select: () => (
            <Select
              onSuccess={handleOptionSelect}
              onClickRegister={() => {
                if (typeof onChangeType === 'function') {
                  onChangeType(Type.Register);
                }
              }}
              onClickSignIn={() => {
                if (typeof onChangeType === 'function') {
                  onChangeType(Type.Login);
                }
              }}
            />
          ),
          loginRegister: () => (
            <>
              <FormContainer>
                <If
                  conditions={{
                    signIn: current === Type.Login,
                    register: current === Type.Register,
                  }}
                >
                  {{
                    signIn: () => (
                      <LoginForm
                        onSubmit={handleLoginSubmit}
                        disable={globalDisable}
                      />
                    ),
                    register: () => (
                      <RegistrationForm
                        onSubmit={handleRegistration}
                        disabled={globalDisable}
                        loading={createAccountStatus === 'loading'}
                      />
                    ),
                  }}
                </If>
              </FormContainer>
              <SocialSignInLabel>
                {current === Type.Login
                  ? 'Or sign in with:'
                  : 'Or sign up with:'}
              </SocialSignInLabel>
              <ButtonList>
                <Google
                  onSuccess={handleGoogleSuccess}
                  disabled={globalDisable}
                />
                <Facebook
                  onSuccess={handleFacebookSuccess}
                  disabled={globalDisable}
                />
                {(current === Type.Login || os === 'iOS') && (
                  <Apple
                    onSuccess={handleAppleSuccess}
                    disabled={globalDisable}
                  />
                )}
              </ButtonList>
              {current === Type.Login && (
                <p>
                  <span>Not a member? </span>
                  <Button
                    styleType="strongLink"
                    onClick={() => {
                      onChangeType && onChangeType(Type.Register);
                    }}
                    data-cy-id="toggle-register"
                  >
                    Sign up
                  </Button>
                </p>
              )}
              {current === Type.Register && (
                <p>
                  <span>Already have an account? </span>
                  <Button
                    styleType="strongLink"
                    onClick={() => onChangeType && onChangeType(Type.Login)}
                    data-cy-id="toggle-login"
                  >
                    Sign in
                  </Button>
                </p>
              )}
            </>
          ),
        }}
      </If>
    </>
  );
};

AuthenticationSelect.defaultProps = {
  current: Type.Login,
};
